import Breadcrumb from '@/components/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useStore } from '@nanostores/react';
import { Cpu, PlusIcon, RefreshCw, CircleDollarSign, LayoutGrid, Table as TableIcon, FileUpIcon } from 'lucide-react';
import { NewAccountReceivableDrawer } from './components/NewAccountReceivableDrawer';
import { NewAccountReceivableModal } from './components/NewAccountReceivableModal';
import { AccountReceivableListing } from './components/AccountReceivableListing';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useRef, useState, useEffect } from 'react';
import { AccountReceivablesService, IntegrationsService, OrganizationService } from '@/api';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { QuickbooksSyncDrawer } from './components/QuickbooksSyncDrawer';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Integrations } from '@/features/settings/components/Integrations';
import { StripeSyncDrawer } from './StripeSyncDrawer';
import { UploadInvoicesModal } from './components/UploadInvoicesModal';

export const ManageAccountReceivables = () => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [showQuickbooksSyncDrawer, setShowQuickbooksSyncDrawer] = useState(false);
  const [showStripeSyncDrawer, setShowStripeSyncDrawer] = useState(false);
  const [viewMode, setViewMode] = useState<'table'>('table');
  const [hasQuickbooks, setHasQuickbooks] = useState(false);
  const [hasCustom, setHasCustom] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showUploadInvoicesModal, setShowUploadInvoicesModal] = useState(false);


  const [hasStripe, setHasStripe] = useState(false);
  const org = useStore($org);
  const po = useStore($portfolio);
  const page = useStore($router);
  

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name
        ? `/organization/${org?.id}/portfolios`
        : '/organizations',
    },
    {
      label: po?.portfolio?.name
        ? `${po.portfolio.name}`
        : 'Portfolio',
      href: po?.portfolio?.name
        ? `/organization/${org?.id}/portfolio/${po?.portfolio.id}/accountreceivables`
        : `/organization/${org?.id}/portfolios`,
    },

    // {
    //   label: 'Projects',
    //   href: `/organization/${org?.id}/workspace/${ws?.workspace.id}/projects`,
    // },
  ];

  const mapping = {
    ["Quickbooks"]: 'Quickbooks',
    ["Stripe"]: 'Stripe',
  }

  const fileInputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!po?.portfolio.id) {
        toast.error("Portfolio ID is required");
        return;
      }

      try {
        const response = await AccountReceivablesService.createAccountReceivableViaCsv({
          portfolioId: po.portfolio.id,
          file: file
        });
        if (response.succeeded) {
          toast.success("Successfully created account receivables via CSV upload");
          queryClient.invalidateQueries({ queryKey: ['accountreceivables'] });
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } else {
          toast.error(response.message || "Failed to upload CSV");
        }
      } catch (error) {
        toast.error("Error uploading CSV");
      }
    }
  };

  const checkIntegrations = async () => {
    if (!org?.id) return;
    const integrations = await IntegrationsService.getProfileIntegrations(org.id);
    setHasQuickbooks(!!integrations.find(i => i.integration?.type === 'Quickbooks'));
    setHasStripe(!!integrations.find(i => i.integration?.type === 'Stripe'));
    setHasCustom(!!integrations.find(i => i.integration?.type === 'Custom'));
  }

  useEffect(() => {
    checkIntegrations();
  }, []);

  const syncStripeProducts = async () => {
    toast.info('Starting sync...');
    const response = await OrganizationService.syncStripeProducts({
      organizationId: org?.id ?? ''
    });
    if (response.succeeded) {
      toast.success('Sync successful');
    } else {
      toast.error('Failed to start sync');
    }
  }

  const syncFromSanaam = async () => {
    toast.info('Starting sync...');
    const response = await OrganizationService.syncSanaam({
      organizationId: org?.id ?? '',
      portfolioId: po?.portfolio.id ?? ''
    });
    if (response.succeeded) {
      toast.success('Sync successful');
    } else {
      toast.error('Failed to start sync');
    }
  }

  return (
    <div className="w-full max-w-full">
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>

      {/* Page Header with actions */}
      <div className="mt-2 flex items-center justify-between space-y-2 w-full">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Accounts</h2>
          <p className="text-muted-foreground">
            {`List of ${po?.portfolio.name}`}
          </p>
        </div>
        <div className="flex items-center space-x-2 flex-wrap justify-end">
          {/* <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'table' | 'kanban')}>
            <TabsList>
              <TabsTrigger value="table">
                <TableIcon className="h-4 w-4 mr-2" />
                Table
              </TabsTrigger>
              <TabsTrigger value="kanban">
                <LayoutGrid className="h-4 w-4 mr-2" />
                Kanban
              </TabsTrigger>
            </TabsList>
          </Tabs> */}

          {org?.name == "Sanaam" && (
            <Button
              onClick={() => syncFromSanaam()}
              disabled={!hasCustom}
              className="ml-2"
          >
              <RefreshCw className="mr-2 h-4 w-4" /> Sync from Sanaam
            </Button>
          )}

          <Button
            onClick={() => syncStripeProducts()}
            disabled={!hasStripe}
            className="ml-2"
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Sync Products from Stripe
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span style={{ display: 'inline-block' }}>
                  <Button
                    onClick={() => setShowStripeSyncDrawer(true)}
                    disabled={!hasStripe}
                    className="ml-2"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" /> Sync Subscriptions from Stripe
                  </Button>
                </span>
              </TooltipTrigger>
              {!hasStripe && (
                <TooltipContent>
                  Please sync your Stripe account in settings page
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <input
            type="file"
            className="hidden"
            accept=".csv"
            onChange={handleFileUpload}
            ref={fileInputRef}
          />
          {hasQuickbooks && (
            <Button
              onClick={() => setShowQuickbooksSyncDrawer(true)}
            >
              <PlusIcon className="mr-2 h-4 w-4" /> Sync from Quickbooks
            </Button>
          )}

          
          <Button onClick={() => setShowUploadInvoicesModal(true)}>
            <FileUpIcon className="mr-2 h-4 w-4" /> Upload
          </Button>

          <Button onClick={() => setShowModal(true)}>
            <PlusIcon className="mr-2 h-4 w-4" /> Add New
          </Button>
        </div>
      </div>

      {/* Account Receivable list*/}
      <div className="mt-4 max-w-full">
        <AccountReceivableListing viewMode={viewMode} />
      </div>

      {/* Create New Project Drawer */}
      <NewAccountReceivableDrawer open={showDrawer} setOpen={setShowDrawer} />
      <NewAccountReceivableModal isOpen={showModal} setOpen={setShowModal} />


      <QuickbooksSyncDrawer open={showQuickbooksSyncDrawer} setOpen={setShowQuickbooksSyncDrawer} />
      <StripeSyncDrawer open={showStripeSyncDrawer} setOpen={setShowStripeSyncDrawer} />
      {showUploadInvoicesModal && (
        <UploadInvoicesModal
          open={showUploadInvoicesModal}
          setOpen={setShowUploadInvoicesModal}
        />
      )}

    </div>
  );
};
